/**
 * Builds the LaunchDarkly context for the signed-in user.
 *
 * This lives outside `FeatureFlags.tsx` for two reasons:
 *
 * 1) It's pure, so it can be unit tested without standing up the LD provider.
 *
 * 2) It's needed in two places: once to *initialize* the LD client with the
 *    identified user (so flags are correct on the very first render) and again
 *    in an effect to re-`identify` if any of the inputs change afterwards.
 */

import type { JwtPayload } from "@granular/fabric3-definitions";
import type { LDContext } from "launchdarkly-react-client-sdk";

export type BuildLdContextArguments = {
  /** Claims from the rendering token. Undefined before the JWT is parsed. */
  user: JwtPayload | undefined;
  /**
   * When impersonating, prefer the *target's* identity so that LD targeting
   * rules written against the target user apply. See `useImpersonation`.
   */
  preferTargetsFeatureFlags: boolean;
  /** Country the app is deployed in — NOT the user's locale. */
  country: string;
  containerName: string;
  isEmbedded: boolean;
  hostname: string;
};

/**
 * Ideally, the JWT would carry the impersonated user's first and last name.
 * Until then, we try and extract them from the email. Since this is super
 * hacky, we are being overly defensive.
 *
 * @param preferTargetsFeatureFlags - Whether to prefer the target's identity
 * @param email - The email to extract the name from
 * @param index - 0 for the given name, 1 for the surname
 * @param defaultName - Returned whenever extraction is not possible
 */
const getNameFromEmail = (
  preferTargetsFeatureFlags: boolean,
  email: string | undefined,
  index: number,
  defaultName: string,
): string => {
  try {
    return preferTargetsFeatureFlags && email?.includes(".")
      ? (email.split("@")[0]?.split(".")[index] ?? defaultName)
      : defaultName;
  } catch {
    /* */
  }

  return defaultName;
};

/**
 * @returns The LD context for the identified user, or `undefined` when we don't
 * (yet) know who the user is. Callers must treat `undefined` as "let LD create
 * an anonymous context" — never invent a key.
 */
export const buildLdContext = ({
  user,
  preferTargetsFeatureFlags,
  country,
  containerName,
  isEmbedded,
  hostname,
}: BuildLdContextArguments): LDContext | undefined => {
  if (!user) {
    return undefined;
  }

  const granularId = preferTargetsFeatureFlags
    ? (user["custom:granular_impersonation_id"] ?? user["custom:granular_id"])
    : user["custom:granular_id"];

  if (!granularId) {
    return undefined;
  }

  const email = preferTargetsFeatureFlags
    ? (user["custom:granular_impersonation_email"] ?? user.email)
    : user.email;

  const givenName = getNameFromEmail(
    preferTargetsFeatureFlags,
    email,
    0,
    user.given_name,
  );
  const surName = getNameFromEmail(
    preferTargetsFeatureFlags,
    email,
    1,
    user.family_name,
  );

  return {
    kind: "user",
    key: `user-key-${granularId}`,
    isEmbedded,
    email,
    country,
    containerName,
    name: `${givenName} ${surName}`,
    hostname,
  };
};
